import React from 'react';
import { type Moment } from 'moment';
import { useIntl } from 'react-intl';
import { Button, Grid, Typography } from '@mui/material';
import { alpha, styled } from '@mui/system';
import { PickersDay, StaticDatePicker, type PickersDayProps } from '@mui/x-date-pickers';

import { parseDate } from '../../Utils';
import useSettings from '../../hooks/useSettings';
import ResponsiveDialog from '../../dialogs/ResponsiveDialog';
import DateRangePresetSelect, { type DateRangePresetType } from '../../fields/components/DateRangePresetSelect';

interface DateRangeFilterDialogProps {
	open: boolean;
	onClose: VoidFunction;
	dateRange: { from?: Date; to?: Date };
	sendChange: (newValue?: { from?: Date; to?: Date }) => void;
}

interface CustomPickersDayProps {
	dayIsBetween?: boolean;
	isFirstDay?: boolean;
	isLastDay?: boolean;
}

type CustomDayProps = PickersDayProps & { dateFrom?: Moment; dateTo?: Moment };

declare module '@mui/x-date-pickers' {
	type StaticDatePickerSlotsComponentsProps = { day: CustomDayProps };
}

const CustomPickersDay = styled(
	({ className, ...props }: CustomDayProps) => (
		<div className={className}>
			<PickersDay {...props} />
		</div>
	),
	{ shouldForwardProp: prop => prop !== 'dayIsBetween' && prop !== 'isFirstDay' && prop !== 'isLastDay' }
)<CustomPickersDayProps>(({ theme, dayIsBetween, isFirstDay, isLastDay }) => ({
	...(dayIsBetween && {
		backgroundColor: alpha(theme.palette.secondary.main, 0.12),
		color: theme.palette.secondary.contrastText,
		'&:hover': {
			backgroundColor: alpha(theme.palette.secondary.dark, 0.12),
			color: theme.palette.secondary.contrastText
		},
		...(isFirstDay && {
			borderTopLeftRadius: '50%',
			borderBottomLeftRadius: '50%'
		}),
		...(isLastDay && {
			borderTopRightRadius: '50%',
			borderBottomRightRadius: '50%'
		})
	}),
	'& > .MuiPickersDay-root': {
		margin: '0 2px',
		...(dayIsBetween && {
			backgroundColor: 'transparent'
		}),
		...((isFirstDay || isLastDay) && {
			'&, &:focus': {
				backgroundColor: theme.palette.secondary.main,
				color: theme.palette.secondary.contrastText
			},
			'&:hover': {
				backgroundColor: theme.palette.secondary.dark,
				color: theme.palette.secondary.contrastText
			}
		})
	}
}));

function CustomDay({ day, dateFrom, dateTo, ...other }: CustomDayProps) {
	if (!dateFrom || !dateTo || other.outsideCurrentMonth) {
		return <PickersDay day={day} {...other} />;
	}

	// Il parametro '[]' serve per includere entrambi gli estremi nel controllo
	const dayIsBetween = day?.isBetween(dateFrom, dateTo, 'day', '[]');
	const isFirstDay = day?.isSame(dateFrom, 'day');
	const isLastDay = day?.isSame(dateTo, 'day');

	return (
		<CustomPickersDay
			{...other}
			day={day}
			disableMargin
			dayIsBetween={dayIsBetween}
			isFirstDay={isFirstDay}
			isLastDay={isLastDay}
		/>
	);
}

function DateRangeFilterDialog(props: DateRangeFilterDialogProps) {
	const { t } = useSettings();
	const intl = useIntl();

	const [dateFrom, setDateFrom] = React.useState<Date | undefined>(props.dateRange.from);
	const [dateTo, setDateTo] = React.useState<Date | undefined>(props.dateRange.to);

	const [editingDate, setEditingDate] = React.useState('from');

	const formattedDateFrom = intl.formatDate(dateFrom, {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit'
	});

	const formattedDateTo = intl.formatDate(dateTo, {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit'
	});

	const displayDate = `${formattedDateFrom} 🠖 ${formattedDateTo}`;

	function handleChange(newDate) {
		if (newDate?.isBefore(dateFrom, 'day')) {
			// If day before date from, sets it
			setDateFrom(newDate);
			setEditingDate('to');
		} else if (newDate?.isAfter(dateTo, 'day')) {
			// If day after date to, sets it
			setDateTo(newDate.endOf('day'));
			setEditingDate('from');
		} else if (editingDate === 'from') {
			// If editing date from, sets it
			setDateFrom(newDate);
			setEditingDate('to');
		} else {
			// If editing date to, sets it
			setDateTo(newDate.endOf('day'));
			setEditingDate('from');
		}

		setPresetRange(undefined);
	}

	function handleDateRangeChange() {
		if (!!dateFrom && !!dateTo) {
			props.sendChange({
				from: parseDate(dateFrom)?.utc().toDate(),
				to: parseDate(dateTo)?.utc().toDate()
			});
		} else props.sendChange(undefined);
		props.onClose();
	}

	function closeDialog() {
		setDateFrom(props.dateRange?.from);
		setDateTo(props.dateRange?.to);
		props.onClose();
	}

	function clear() {
		setDateFrom(undefined);
		setDateTo(undefined);
		props.sendChange();
		props.onClose();
	}

	const [presetRange, setPresetRange] = React.useState<DateRangePresetType>();

	function handlePresetRangeChange(newValue?: DateRangePresetType) {
		setPresetRange(newValue);
		setDateFrom(newValue?.range.from.toDate());
		setDateTo(newValue?.range.to.toDate());
	}

	return (
		<ResponsiveDialog
			open={props.open}
			onClose={props.onClose}
			maxWidth="md"
			fullWidth={false}
			title={
				<Grid container direction="column" spacing={2}>
					<Grid size={12}>
						<Typography variant="h6">
							{dateFrom && dateTo ? displayDate : t('DIALOG_TITLE_DATERANGE')}
						</Typography>
					</Grid>
					<Grid size={12}>
						<DateRangePresetSelect value={presetRange} sendChange={handlePresetRangeChange} />
					</Grid>
				</Grid>
			}
			content={
				<StaticDatePicker
					slots={{ day: CustomDay }}
					slotProps={{
						day: { dateFrom, dateTo } as CustomDayProps,
						actionBar: { actions: [] }
					}}
					displayStaticWrapperAs="desktop"
					value={null}
					onChange={handleChange}
					displayWeekNumber
				/>
			}
			actions={
				<>
					<Button onClick={closeDialog}>{t('DIALOG_ACTION_BACK')}</Button>
					<Button onClick={clear}>{t('DIALOG_ACTION_CLEAR')}</Button>
					<Button color="secondary" onClick={handleDateRangeChange}>
						{t('DIALOG_ACTION_CONFIRM')}
					</Button>
				</>
			}
		/>
	);
}

export default React.memo(DateRangeFilterDialog);
